/**
 * Análises › Painéis padrão (D522; doc 20 §4.3): the eight standard dashboards as DATA over the governed catalog —
 * visão geral, food, corridas, entregas, Balcão (empresas), financeiro, motoristas e entregadores, clientes.
 *
 * A panel is a few QUERY BLOCKS, never SQL: KPI blocks (headline figures with the comparison), one trend block (the curve over
 * the period) and one or two breakdowns (a ranking by a dimension). Blocks are split so the planner can always answer them —
 * a block never mixes money (grouped by currency) with counts, and never mixes measures that live in different fact tables —
 * and `bi-panels.test.ts` runs every block of every panel through the same planner the server uses.
 *
 * No block asks for a person (a customer, driver or courier name): the standard panels carry numbers only, so they work with
 * `analytics:view` alone. People appear only in the Explorador and only with `analytics:view_personal`.
 */
import { type DimensionDef, type Granularity, type QueryFilter, type QueryRequest, getDimension, operatorsFor, planQuery } from '@vello/analytics-semantic';
import { type BiCompareChoice, type BiPeriod, compareFields, periodRequest } from './bi-core.js';
import { type CompareAgainst } from '@vello/analytics-semantic';
import { type ExplorerState, defaultExplorerState } from './bi-explorer.js';
import { type BiInsightDef, FINANCE_INSIGHTS, GROWTH_INSIGHTS, MAP_INSIGHTS, OPERATION_INSIGHTS, QUALITY_INSIGHTS, SUPPLY_INSIGHTS } from './bi-insights.js';

/** The eight standard dashboards of D522, then the five market-standard analyses of D531 (Operação, Crescimento, Oferta, Mapa, Qualidade). */
export const BI_PANEL_IDS = ['overview', 'food', 'rides', 'deliveries', 'desk', 'finance', 'people', 'customers', 'operation', 'growth', 'supply', 'map', 'quality'] as const;
export type BiPanelId = (typeof BI_PANEL_IDS)[number];

export interface BiBlock {
  readonly id: string;
  readonly metrics: readonly string[];
  readonly dimensions?: readonly string[];
  readonly filters?: readonly QueryFilter[];
  readonly sort?: readonly { readonly by: string; readonly direction: 'asc' | 'desc' }[];
  readonly limit?: number;
  /** Ask for the group totals (the KPIs of a block that is also a ranking). */
  readonly totals?: boolean;
}

export interface BiBreakdown {
  readonly id: string;
  readonly titleId: string;
  readonly block: BiBlock;
  readonly metric: string;
  readonly dimension: string;
  readonly chart: 'bars' | 'donut';
}

export interface BiPanelDef {
  readonly id: BiPanelId;
  readonly labelId: string;
  readonly subtitleId: string;
  /** Headline figures, in order; each block yields the cards of its metrics. */
  readonly kpis: readonly BiBlock[];
  readonly trend: { readonly block: BiBlock; readonly metric: string; readonly titleId: string };
  readonly breakdowns: readonly BiBreakdown[];
  /** D531: the market-standard analyses of the panel (charts beyond the KPIs, the trend and the breakdowns). */
  readonly insights?: readonly BiInsightDef[];
  /** D531: the panel's source has no hour of the day (the pickup-cell view is by day): an hourly curve is drawn by day. */
  readonly dayGrainOnly?: boolean;
}

/** The grain a panel draws its curves at: the screen's, except for a source that has no hour. */
export const panelGrain = (panel: Pick<BiPanelDef, 'dayGrainOnly'>, grain: Granularity): Granularity => (panel.dayGrainOnly === true && grain === 'hour' ? 'day' : grain);

const vertical = (v: 'FOOD' | 'RIDE' | 'DELIVERY'): QueryFilter => ({ dimension: 'vertical', op: 'eq', values: [v] });
const desc = (by: string): { by: string; direction: 'desc' } => ({ by, direction: 'desc' });

export const BI_PANELS: Readonly<Record<BiPanelId, BiPanelDef>> = {
  overview: {
    id: 'overview',
    labelId: 'bi.panel.overview',
    subtitleId: 'bi.panel.overview.sub',
    kpis: [
      { id: 'counts', metrics: ['orders', 'completion_rate', 'cancellation_rate'] },
      { id: 'money', metrics: ['gmv', 'commission'] },
    ],
    trend: { block: { id: 'trend', metrics: ['orders'] }, metric: 'orders', titleId: 'bi.trend.orders' },
    breakdowns: [
      { id: 'byVertical', titleId: 'bi.break.ordersByVertical', block: { id: 'byVertical', metrics: ['orders'], dimensions: ['vertical'], sort: [desc('orders')], limit: 10 }, metric: 'orders', dimension: 'vertical', chart: 'donut' },
      { id: 'byMarket', titleId: 'bi.break.ordersByMarket', block: { id: 'byMarket', metrics: ['orders'], dimensions: ['market'], sort: [desc('orders')], limit: 10 }, metric: 'orders', dimension: 'market', chart: 'bars' },
    ],
  },
  food: {
    id: 'food',
    labelId: 'bi.panel.food',
    subtitleId: 'bi.panel.food.sub',
    kpis: [
      { id: 'counts', metrics: ['orders', 'completion_rate', 'cancellation_rate', 'prep_time'], filters: [vertical('FOOD')] },
      { id: 'money', metrics: ['gmv', 'average_ticket', 'commission'], filters: [vertical('FOOD')] },
    ],
    trend: { block: { id: 'trend', metrics: ['orders'], filters: [vertical('FOOD')] }, metric: 'orders', titleId: 'bi.trend.orders' },
    breakdowns: [
      { id: 'byMerchant', titleId: 'bi.break.topMerchants', block: { id: 'byMerchant', metrics: ['orders'], dimensions: ['merchant'], filters: [vertical('FOOD')], sort: [desc('orders')], limit: 5 }, metric: 'orders', dimension: 'merchant', chart: 'bars' },
      { id: 'byDayPart', titleId: 'bi.break.ordersByDayPart', block: { id: 'byDayPart', metrics: ['orders'], dimensions: ['day_part'], filters: [vertical('FOOD')] }, metric: 'orders', dimension: 'day_part', chart: 'donut' },
    ],
  },
  rides: {
    id: 'rides',
    labelId: 'bi.panel.rides',
    subtitleId: 'bi.panel.rides.sub',
    kpis: [
      { id: 'counts', metrics: ['rides_requested', 'rides_completed', 'rides_no_driver', 'assignment_rate', 'time_to_assign'] },
      { id: 'money', metrics: ['gmv', 'average_ticket'], filters: [vertical('RIDE')] },
    ],
    trend: { block: { id: 'trend', metrics: ['rides_requested'] }, metric: 'rides_requested', titleId: 'bi.trend.rides' },
    breakdowns: [
      { id: 'byMarket', titleId: 'bi.break.ridesByMarket', block: { id: 'byMarket', metrics: ['rides_requested'], dimensions: ['market'], sort: [desc('rides_requested')], limit: 10 }, metric: 'rides_requested', dimension: 'market', chart: 'bars' },
      { id: 'byCancel', titleId: 'bi.break.cancelReasons', block: { id: 'byCancel', metrics: ['orders_cancelled'], dimensions: ['cancel_reason'], filters: [vertical('RIDE')], sort: [desc('orders_cancelled')], limit: 6 }, metric: 'orders_cancelled', dimension: 'cancel_reason', chart: 'bars' },
    ],
  },
  deliveries: {
    id: 'deliveries',
    labelId: 'bi.panel.deliveries',
    subtitleId: 'bi.panel.deliveries.sub',
    kpis: [
      { id: 'counts', metrics: ['orders', 'completion_rate', 'cancellation_rate', 'delivery_time'], filters: [vertical('DELIVERY')] },
      { id: 'money', metrics: ['gmv', 'average_ticket'], filters: [vertical('DELIVERY')] },
    ],
    trend: { block: { id: 'trend', metrics: ['orders'], filters: [vertical('DELIVERY')] }, metric: 'orders', titleId: 'bi.trend.orders' },
    breakdowns: [
      { id: 'byMarket', titleId: 'bi.break.ordersByMarket', block: { id: 'byMarket', metrics: ['orders'], dimensions: ['market'], filters: [vertical('DELIVERY')], sort: [desc('orders')], limit: 10 }, metric: 'orders', dimension: 'market', chart: 'bars' },
      { id: 'byCancel', titleId: 'bi.break.cancelReasons', block: { id: 'byCancel', metrics: ['orders_cancelled'], dimensions: ['cancel_reason'], filters: [vertical('DELIVERY')], sort: [desc('orders_cancelled')], limit: 6 }, metric: 'orders_cancelled', dimension: 'cancel_reason', chart: 'bars' },
    ],
  },
  desk: {
    id: 'desk',
    labelId: 'bi.panel.desk',
    subtitleId: 'bi.panel.desk.sub',
    kpis: [
      // Orders placed by partner accounts: a ranking by account whose TOTALS are the headline (a dimension only partner rows carry).
      { id: 'counts', metrics: ['orders', 'orders_cancelled'], dimensions: ['partner_account'], totals: true, sort: [desc('orders')], limit: 5 },
      { id: 'money', metrics: ['partner_spend_settled', 'partner_spend_authorized'] },
    ],
    trend: { block: { id: 'trend', metrics: ['partner_spend_settled'] }, metric: 'partner_spend_settled', titleId: 'bi.trend.partnerSpend' },
    breakdowns: [
      { id: 'byAccount', titleId: 'bi.break.topCompanies', block: { id: 'byAccount', metrics: ['orders'], dimensions: ['partner_account'], sort: [desc('orders')], limit: 5 }, metric: 'orders', dimension: 'partner_account', chart: 'bars' },
      { id: 'spendByAccount', titleId: 'bi.break.spendByCompany', block: { id: 'spendByAccount', metrics: ['partner_spend_settled'], dimensions: ['partner_account'], sort: [desc('partner_spend_settled')], limit: 5 }, metric: 'partner_spend_settled', dimension: 'partner_account', chart: 'bars' },
    ],
  },
  finance: {
    id: 'finance',
    labelId: 'bi.panel.finance',
    subtitleId: 'bi.panel.finance.sub',
    kpis: [{ id: 'money', metrics: ['gmv', 'commission', 'take_rate', 'discounts', 'partner_net'] }],
    insights: FINANCE_INSIGHTS,
    trend: { block: { id: 'trend', metrics: ['gmv'] }, metric: 'gmv', titleId: 'bi.trend.gmv' },
    breakdowns: [
      { id: 'gmvByVertical', titleId: 'bi.break.gmvByVertical', block: { id: 'gmvByVertical', metrics: ['gmv'], dimensions: ['vertical'], sort: [desc('gmv')], limit: 10 }, metric: 'gmv', dimension: 'vertical', chart: 'bars' },
      { id: 'commissionByMarket', titleId: 'bi.break.commissionByMarket', block: { id: 'commissionByMarket', metrics: ['commission'], dimensions: ['market'], sort: [desc('commission')], limit: 10 }, metric: 'commission', dimension: 'market', chart: 'bars' },
    ],
  },
  people: {
    id: 'people',
    labelId: 'bi.panel.people',
    subtitleId: 'bi.panel.people.sub',
    kpis: [{ id: 'counts', metrics: ['active_drivers', 'active_couriers', 'orders_completed', 'time_to_assign'] }],
    trend: { block: { id: 'trend', metrics: ['active_drivers'] }, metric: 'active_drivers', titleId: 'bi.trend.activeDrivers' },
    breakdowns: [
      { id: 'driversByMarket', titleId: 'bi.break.driversByMarket', block: { id: 'driversByMarket', metrics: ['active_drivers'], dimensions: ['market'], sort: [desc('active_drivers')], limit: 10 }, metric: 'active_drivers', dimension: 'market', chart: 'bars' },
      { id: 'completedByDayPart', titleId: 'bi.break.completedByDayPart', block: { id: 'completedByDayPart', metrics: ['orders_completed'], dimensions: ['day_part'] }, metric: 'orders_completed', dimension: 'day_part', chart: 'donut' },
    ],
  },
  customers: {
    id: 'customers',
    labelId: 'bi.panel.customers',
    subtitleId: 'bi.panel.customers.sub',
    kpis: [
      { id: 'counts', metrics: ['active_customers', 'orders'] },
      { id: 'money', metrics: ['average_ticket', 'gmv'] },
    ],
    trend: { block: { id: 'trend', metrics: ['active_customers'] }, metric: 'active_customers', titleId: 'bi.trend.activeCustomers' },
    breakdowns: [
      { id: 'byChannel', titleId: 'bi.break.ordersByChannel', block: { id: 'byChannel', metrics: ['orders'], dimensions: ['channel'], sort: [desc('orders')], limit: 6 }, metric: 'orders', dimension: 'channel', chart: 'donut' },
      { id: 'byPayment', titleId: 'bi.break.ordersByPayment', block: { id: 'byPayment', metrics: ['orders'], dimensions: ['payment_method'], sort: [desc('orders')], limit: 6 }, metric: 'orders', dimension: 'payment_method', chart: 'bars' },
    ],
  },
  // ---- D531: the market-standard analyses ----
  operation: {
    id: 'operation',
    labelId: 'bi.panel.operation',
    subtitleId: 'bi.panel.operation.sub',
    kpis: [
      { id: 'stages', metrics: ['accept_time', 'pickup_time', 'trip_time', 'prep_time'] },
      { id: 'eta', metrics: ['eta_on_time_rate', 'eta_deviation', 'eta_compared'] },
    ],
    insights: OPERATION_INSIGHTS,
    trend: { block: { id: 'trend', metrics: ['eta_on_time_rate'] }, metric: 'eta_on_time_rate', titleId: 'bi.trend.etaOnTime' },
    breakdowns: [
      { id: 'pickupByVertical', titleId: 'bi.break.pickupByVertical', block: { id: 'pickupByVertical', metrics: ['pickup_time'], dimensions: ['vertical'], sort: [desc('pickup_time')], limit: 10 }, metric: 'pickup_time', dimension: 'vertical', chart: 'bars' },
      { id: 'onTimeByMarket', titleId: 'bi.break.onTimeByMarket', block: { id: 'onTimeByMarket', metrics: ['eta_on_time_rate'], dimensions: ['market'], sort: [desc('eta_on_time_rate')], limit: 10 }, metric: 'eta_on_time_rate', dimension: 'market', chart: 'bars' },
    ],
  },
  growth: {
    id: 'growth',
    labelId: 'bi.panel.growth',
    subtitleId: 'bi.panel.growth.sub',
    kpis: [
      { id: 'people', metrics: ['new_customers', 'returning_customers', 'orders_per_customer'] },
      { id: 'orders', metrics: ['first_orders', 'repeat_orders'] },
      { id: 'quotes', metrics: ['quotes', 'quotes_converted', 'quote_conversion_rate'] },
    ],
    insights: GROWTH_INSIGHTS,
    trend: { block: { id: 'trend', metrics: ['new_customers'] }, metric: 'new_customers', titleId: 'bi.trend.newCustomers' },
    breakdowns: [
      { id: 'byCustomerType', titleId: 'bi.break.ordersByCustomerType', block: { id: 'byCustomerType', metrics: ['orders'], dimensions: ['customer_type'], sort: [desc('orders')], limit: 4 }, metric: 'orders', dimension: 'customer_type', chart: 'donut' },
      { id: 'newByMarket', titleId: 'bi.break.newByMarket', block: { id: 'newByMarket', metrics: ['new_customers'], dimensions: ['market'], sort: [desc('new_customers')], limit: 10 }, metric: 'new_customers', dimension: 'market', chart: 'bars' },
    ],
  },
  supply: {
    id: 'supply',
    labelId: 'bi.panel.supply',
    subtitleId: 'bi.panel.supply.sub',
    kpis: [
      { id: 'time', metrics: ['supply_online_time', 'supply_idle_time', 'supply_busy_time'] },
      { id: 'use', metrics: ['supply_utilisation', 'supply_avg_drivers'] },
      { id: 'offers', metrics: ['offers_decided', 'offer_acceptance_rate', 'offer_decline_rate', 'offer_lapse_rate'] },
    ],
    insights: SUPPLY_INSIGHTS,
    trend: { block: { id: 'trend', metrics: ['supply_online_time'] }, metric: 'supply_online_time', titleId: 'bi.trend.supplyTime' },
    breakdowns: [
      { id: 'useByDayPart', titleId: 'bi.break.useByDayPart', block: { id: 'useByDayPart', metrics: ['supply_utilisation'], dimensions: ['day_part'] }, metric: 'supply_utilisation', dimension: 'day_part', chart: 'bars' },
      { id: 'offersByStatus', titleId: 'bi.break.offersByStatus', block: { id: 'offersByStatus', metrics: ['offers'], dimensions: ['offer_status'], sort: [desc('offers')], limit: 8 }, metric: 'offers', dimension: 'offer_status', chart: 'donut' },
    ],
  },
  map: {
    id: 'map',
    labelId: 'bi.panel.map',
    subtitleId: 'bi.panel.map.sub',
    kpis: [{ id: 'cells', metrics: ['cell_orders'] }],
    dayGrainOnly: true,
    insights: MAP_INSIGHTS,
    trend: { block: { id: 'trend', metrics: ['cell_orders'] }, metric: 'cell_orders', titleId: 'bi.trend.cellOrders' },
    breakdowns: [{ id: 'cellsByWeekday', titleId: 'bi.break.cellsByWeekday', block: { id: 'cellsByWeekday', metrics: ['cell_orders'], dimensions: ['weekday'] }, metric: 'cell_orders', dimension: 'weekday', chart: 'bars' }],
  },
  quality: {
    id: 'quality',
    labelId: 'bi.panel.quality',
    subtitleId: 'bi.panel.quality.sub',
    kpis: [
      { id: 'ratings', metrics: ['rating_average', 'ratings', 'low_rating_share'] },
      { id: 'issues', metrics: ['issues', 'issues_resolved', 'refunds'] },
      { id: 'money', metrics: ['refund_amount', 'issue_amount'] },
      { id: 'stores', metrics: ['store_availability_rate', 'store_pauses', 'store_live_seconds'] },
    ],
    insights: QUALITY_INSIGHTS,
    trend: { block: { id: 'trend', metrics: ['rating_average'] }, metric: 'rating_average', titleId: 'bi.trend.ratingAverage' },
    breakdowns: [
      { id: 'issuesByReason', titleId: 'bi.break.issuesByReason', block: { id: 'issuesByReason', metrics: ['issues'], dimensions: ['issue_reason'], sort: [desc('issues')], limit: 8 }, metric: 'issues', dimension: 'issue_reason', chart: 'bars' },
      { id: 'issuesByKind', titleId: 'bi.break.issuesByKind', block: { id: 'issuesByKind', metrics: ['issues'], dimensions: ['issue_kind'], sort: [desc('issues')], limit: 8 }, metric: 'issues', dimension: 'issue_kind', chart: 'donut' },
    ],
  },
};

/** D528: the global filter bar of a panel: conditions that apply to EVERY block (KPIs, trend and breakdowns). */
export interface PanelBar {
  readonly filters: readonly QueryFilter[];
  readonly anyOf: readonly (readonly QueryFilter[])[];
}

export interface PanelQueryContext {
  readonly period: BiPeriod;
  readonly compare: BiCompareChoice;
  readonly upToNowAgainst?: CompareAgainst;
  /** The scope filters of the operator's switcher. */
  readonly scope: readonly QueryFilter[];
  /** D528: the global filter bar. */
  readonly bar?: PanelBar;
  /** `true` once the automatic comparison has fallen back to the previous period. */
  readonly fallback?: boolean;
}

/** The request of one block of a panel (the scope filters first, so a block's own filter on the same dimension never doubles them). */
export function blockRequest(block: BiBlock, ctx: PanelQueryContext, extra: { readonly granularity?: Granularity; readonly comparison?: boolean; readonly ignoreBar?: string } = {}): QueryRequest {
  const own = block.filters ?? [];
  // D531: a block's own EXCLUSION (the bridges leave rides out) is a limit, not a pin: the bar can still choose among what is left, and the two are ANDed.
  const pins = own.filter((o) => o.op !== 'not_in');
  const full = ctx.bar ?? { filters: [], anyOf: [] };
  // A chart of ONE field keeps showing all of that field's values while the bar is filtered to some of them (the chosen marks read as selected):
  // otherwise the first click would leave a single bar to click. Every other figure and chart is filtered.
  const keepsAll = (f: QueryFilter): boolean => extra.ignoreBar !== undefined && f.dimension === extra.ignoreBar && (f.op === 'in' || f.op === 'eq');
  const bar = { filters: full.filters.filter((f) => !keepsAll(f)), anyOf: full.anyOf.map((g) => g.filter((f) => !keepsAll(f))).filter((g) => g.length > 0) };
  // The bar's own condition on a market or a country replaces the switcher's on the same dimension (they can narrow within the scope; the
  // server's reach is what finally bounds it). A dimension the block pins itself (the Food panel's `vertical`) is the panel's, not the bar's.
  const barDims = new Set([...bar.filters, ...bar.anyOf.flat()].map((f) => f.dimension));
  const scope = ctx.scope.filter((f) => !own.some((o) => o.dimension === f.dimension) && !barDims.has(f.dimension));
  const pinned = (f: QueryFilter): boolean => pins.some((o) => o.dimension === f.dimension);
  const barFilters = bar.filters.filter((f) => !pinned(f));
  const barGroups = bar.anyOf.map((g) => g.filter((f) => !pinned(f))).filter((g) => g.length > 0);
  const filters = [...scope, ...own, ...barFilters];
  return {
    metrics: [...block.metrics],
    ...(block.dimensions !== undefined ? { dimensions: [...block.dimensions] } : {}),
    ...(filters.length > 0 ? { filters: filters.map((f) => ({ ...f, values: [...f.values] })) } : {}),
    ...(barGroups.length > 0 ? { anyOf: barGroups.map((g) => g.map((f) => ({ ...f, values: [...f.values] }))) } : {}),
    period: periodRequest(ctx.period),
    ...(extra.granularity !== undefined && extra.granularity !== 'total' ? { granularity: extra.granularity } : {}),
    ...(extra.comparison === false ? {} : compareFields(ctx.compare, ctx.upToNowAgainst, ctx.fallback === true)),
    ...(block.sort !== undefined ? { sort: block.sort.map((s) => ({ ...s })) } : {}),
    ...(block.limit !== undefined ? { limit: block.limit } : {}),
    ...(block.totals === true ? { totals: true } : {}),
  };
}

/** Every request a panel makes, for the planner test and for the loader. */
export function panelRequests(panel: BiPanelDef, ctx: PanelQueryContext, screenGrain: Granularity): { readonly kpis: readonly QueryRequest[]; readonly trend: QueryRequest; readonly breakdowns: readonly QueryRequest[]; readonly insights: readonly QueryRequest[] } {
  const grain = panelGrain(panel, screenGrain);
  return {
    kpis: panel.kpis.map((b) => blockRequest(b, ctx)),
    trend: blockRequest(panel.trend.block, ctx, { granularity: grain }),
    breakdowns: panel.breakdowns.map((b) => blockRequest(b.block, ctx, { ignoreBar: b.dimension })),
    insights: (panel.insights ?? []).flatMap((i) => insightRequests(i, ctx, grain).map((r) => r.request)),
  };
}

/** The requests of one insight (D531): its blocks, each with the screen's period, scope and bar; the comparison only where the insight asked for it, the time grain only where it needs one. */
export function insightRequests(insight: BiInsightDef, ctx: PanelQueryContext, grain: Granularity): readonly { readonly id: string; readonly request: QueryRequest }[] {
  return insight.blocks.map((b) => ({ id: b.id, request: insightBlockRequest(b, ctx, grain) }));
}

/** One block of an insight as a request (see {@link insightRequests}). */
export function insightBlockRequest(b: BiInsightDef['blocks'][number], ctx: PanelQueryContext, grain: Granularity): QueryRequest {
  return blockRequest(b.block, ctx, {
    ...(b.grain === true ? { granularity: grain } : {}),
    ...(b.comparison === true ? {} : { comparison: false }),
    ...(b.ignoreBar !== undefined ? { ignoreBar: b.ignoreBar } : {}),
  });
}

// ───────────────────────────── D528: which dimensions the global filter bar can take on this panel ─────────────────────────────

const SAMPLE: Readonly<Record<string, string>> = {
  year: '2026', quarter: '2026-Q1', month: '2026-03', day: '2026-03-01', hour: '08', weekday: '2', dayPart: 'MORNING', market: 'zz:alpha', country: 'ZZ', region: 'ZZ-AA', digits: '1',
  currency: 'XAA', vertical: 'FOOD', code: 'APP', ref: 'x', orderRef: 'FOOD:x', flag: 'true', count: '1', money: '1', duration: '1', distance: '1',
};

/** A valid condition on the dimension, only to ask the planner whether the panel's blocks can carry it. */
export function probeCondition(def: DimensionDef): QueryFilter {
  const operators = operatorsFor(def);
  if (operators.includes('not_null')) return { dimension: def.id, op: 'not_null', values: [] };
  return { dimension: def.id, op: 'in', values: [SAMPLE[def.valueType] ?? 'x'] };
}

export type PanelCompat = { readonly ok: true } | { readonly ok: false; readonly code: string; readonly block: string };

const INCOMPATIBLE = new Set(['FILTER_NOT_AVAILABLE_FOR_METRICS', 'DIMENSIONS_NOT_COMBINABLE', 'DIMENSION_NOT_AVAILABLE_FOR_METRICS', 'METRICS_SPAN_FACT_TABLES']);

/**
 * Can EVERY block of this panel carry a condition on the dimension? The planner is the authority (the very function the server runs, on the
 * very blocks the panel asks): the answer is the SERVER's reason code - `FILTER_NOT_AVAILABLE_FOR_METRICS` when no table carries the
 * dimension beside a block's measures - or `PANEL_PINS_DIMENSION` when a block fixes that dimension itself. The bar applies to all blocks or to
 * none, so a figure and the chart beside it are never answers to different questions.
 */
export function panelDimensionCompat(panel: BiPanelDef, dimensionId: string, clock: { readonly asOf: string; readonly today: string }): PanelCompat {
  const def = getDimension(dimensionId);
  if (def === undefined) return { ok: false, code: 'DIMENSION_UNKNOWN', block: '' };
  const probe = probeCondition(def);
  const ctx: PanelQueryContext = { period: { kind: 'preset', preset: 'last_7_days' }, compare: 'none', scope: [], bar: { filters: [probe], anyOf: [] } };
  const blocks: { id: string; block: BiBlock; grain?: Granularity }[] = [
    ...panel.kpis.map((block) => ({ id: `kpi:${block.id}`, block })),
    { id: 'trend', block: panel.trend.block, grain: 'day' as const },
    ...panel.breakdowns.map((b) => ({ id: `break:${b.id}`, block: b.block })),
    // D531: the insights are blocks like the others - the bar applies to all or to none
    ...(panel.insights ?? []).flatMap((i) => i.blocks.map((b) => ({ id: `ins:${i.id}:${b.id}`, block: b.block, ...(b.grain === true ? { grain: 'day' as const } : {}) }))),
  ];
  for (const { id, block, grain } of blocks) {
    if ((block.filters ?? []).some((f) => f.dimension === dimensionId && f.op !== 'not_in')) return { ok: false, code: 'PANEL_PINS_DIMENSION', block: id };
    const planned = planQuery(blockRequest(block, ctx, grain !== undefined ? { granularity: grain, comparison: false } : { comparison: false }), clock);
    if (!planned.ok && INCOMPATIBLE.has(planned.code)) return { ok: false, code: planned.code, block: id };
  }
  return { ok: true };
}

/**
 * D531: the way DOWN from an insight - the Explorer state that asks the insight's drill block with the same period, comparison, scope and bar,
 * so "see the rows" shows the very figures the chart drew (and the operator can cut them further, export or save them).
 */
export function insightDrillState(insight: BiInsightDef, ctx: PanelQueryContext, grain: Granularity): ExplorerState {
  const chosen = insight.blocks.find((b) => b.id === insight.drill) ?? (insight.blocks[0] as BiInsightDef['blocks'][number]);
  const request = insightBlockRequest(chosen, ctx, grain);
  const spec = (f: QueryFilter): { dimension: string; op: NonNullable<QueryFilter['op']>; values: readonly string[]; currency?: string } => ({
    dimension: f.dimension,
    op: f.op ?? 'in',
    values: [...f.values],
    ...(f.currency !== undefined ? { currency: f.currency } : {}),
  });
  const first = request.sort?.[0];
  return {
    ...defaultExplorerState(),
    mode: 'table',
    metrics: [...request.metrics],
    dimensions: [...(request.dimensions ?? [])],
    grain: request.granularity ?? 'total',
    filters: (request.filters ?? []).map(spec),
    anyOf: (request.anyOf ?? []).map((g) => g.map(spec)),
    period: ctx.period,
    compare: ctx.compare,
    upToNowAgainst: ctx.upToNowAgainst ?? 'last_year_by_date',
    ...(first !== undefined ? { sortBy: first.by, sortDir: first.direction ?? 'desc' } : {}),
  };
}

/** The panel a link names (`?panel=operation`), if it is one of ours. */
export function panelFromSearch(search: string): BiPanelId | undefined {
  const id = new URLSearchParams(search).get('panel');
  return (BI_PANEL_IDS as readonly string[]).includes(id ?? '') ? (id as BiPanelId) : undefined;
}
